import type { ReactElement } from "react"
import { Box, Typography, useTheme } from "@mui/material"
import DiscountChip from "@/components/common/DiscountChip"
import { sanitizeNonNegativeMoney } from "@/components/pricing/sanitize-money"
import type { CatalogPricingProjection, CatalogPromotion } from "@/models/menu-item-v2"
import { formatMoney as formatMoneyDefault } from "@/utils/locale-format"
import { getDesignTokens } from "@/theme/designTokens"

export type PriceDisplayProps = {
  /** Authoritative catalog pricing projection from the backend. */
  pricingProjection?: CatalogPricingProjection | null
  /** Sibling promotion metadata; badge_text is Offer-authored only. */
  promotion?: CatalogPromotion | null
  /**
   * Compatibility fallback when `pricingProjection` is missing (Case E).
   * Must be the existing legacy unit price — never a client-calculated amount.
   */
  legacyItemPrice?: number | null
  formatMoney?: (amount: number) => string
  /** Dense layout for menu rows; default matches catalog card weight. */
  size?: "default" | "compact"
}

function resolveBadgeText(promotion: CatalogPromotion | null | undefined): string | null {
  if (promotion == null) return null
  const raw = promotion.badge_text
  if (raw == null) return null
  const text = String(raw).trim()
  return text === "" ? null : text
}

/**
 * Sole approved catalog unit-price presenter.
 * Renders backend `pricing_projection` + `promotion` only — never calculates prices or badges.
 */
export function PriceDisplay({
  pricingProjection = null,
  promotion = null,
  legacyItemPrice = null,
  formatMoney = formatMoneyDefault,
  size = "default",
}: PriceDisplayProps): ReactElement | null {
  const theme = useTheme()
  const tokens = getDesignTokens(theme)
  const compact = size === "compact"
  const sellingFont = compact ? theme.typography.pxToRem(14) : theme.typography.pxToRem(15)
  const strikeFont = compact ? theme.typography.pxToRem(12) : theme.typography.pxToRem(13)
  const badgeText = resolveBadgeText(promotion)

  // Case E — missing projection → legacy item_price only
  if (pricingProjection == null) {
    if (legacyItemPrice === null || legacyItemPrice === undefined) {
      return null
    }
    const amount = sanitizeNonNegativeMoney(legacyItemPrice)
    const label = formatMoney(amount)
    return (
      <Box
        data-testid="price-display"
        aria-label={`Price ${label}`}
        sx={{
          display: "inline-flex",
          flexWrap: "wrap",
          alignItems: "baseline",
          gap: 0.75,
          minWidth: 0,
          maxWidth: "100%",
        }}
      >
        <Typography
          component="span"
          data-testid="price-display-selling"
          aria-hidden
          sx={{
            m: 0,
            fontSize: sellingFont,
            fontWeight: theme.typography.fontWeightBold,
            color: theme.palette.text.primary,
            fontVariantNumeric: "tabular-nums",
            fontFeatureSettings: '"tnum" 1',
            lineHeight: 1.2,
            wordBreak: "break-word",
          }}
        >
          {label}
        </Typography>
      </Box>
    )
  }

  // Cases A/B — projection present. list_price and selling_price_basis are intentionally unused.
  const channel = sanitizeNonNegativeMoney(pricingProjection.channel_price)
  const selling = sanitizeNonNegativeMoney(pricingProjection.selling_price)
  const showStrike = channel !== selling
  const sellingLabel = formatMoney(selling)
  const channelLabel = formatMoney(channel)

  const ariaLabel = showStrike
    ? `Price ${sellingLabel}, was ${channelLabel}`
    : `Price ${sellingLabel}`

  return (
    <Box
      data-testid="price-display"
      role="group"
      aria-label={ariaLabel}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 0,
        minWidth: 0,
        maxWidth: "100%",
      }}
    >
      <Box
        sx={{
          display: "inline-flex",
          flexWrap: "wrap",
          alignItems: "baseline",
          gap: 0.75,
          minWidth: 0,
          maxWidth: "100%",
        }}
      >
        {showStrike ? (
          <Typography
            component="span"
            data-testid="price-display-channel"
            aria-hidden
            sx={{
              m: 0,
              fontSize: strikeFont,
              fontWeight: theme.typography.fontWeightRegular,
              color: theme.palette.text.secondary,
              textDecoration: "line-through",
              fontVariantNumeric: "tabular-nums",
              fontFeatureSettings: '"tnum" 1',
              lineHeight: 1.2,
              wordBreak: "break-word",
            }}
          >
            {channelLabel}
          </Typography>
        ) : null}

        <Typography
          component="span"
          data-testid="price-display-selling"
          aria-hidden
          sx={{
            m: 0,
            fontSize: sellingFont,
            fontWeight: theme.typography.fontWeightBold,
            color: theme.palette.text.primary,
            fontVariantNumeric: "tabular-nums",
            fontFeatureSettings: '"tnum" 1',
            lineHeight: 1.2,
            wordBreak: "break-word",
          }}
        >
          {sellingLabel}
        </Typography>
      </Box>

      {badgeText ? (
        <Box
          data-testid="price-display-badge"
          sx={{
            display: "inline-flex",
            maxWidth: "100%",
            minWidth: 0,
            mt: `${tokens.tag.promo.gapFromPricePx}px`,
          }}
        >
          <DiscountChip
            label={badgeText}
            size="default"
            interactive={false}
          />
        </Box>
      ) : null}
    </Box>
  )
}

export default PriceDisplay
